import InputIcon from "@mui/icons-material/Input";
import { SortingState } from "@tanstack/react-table";
import TableContainer2 from "Common/Tables/ObjectFiltersTable/TableContainer2";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import {
  DynamicMenuItem,
  MenuItemConfig,
} from "components/Menuitem/DynamicMenuItem";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";
import usePermission from "CustomHooks/usePermission";
import { api } from "helpers/api_url";
import { app_modules } from "helpers/apps_modules_list";
import { useEffect, useMemo } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import { fetchPendingStockTransferList } from "slices/Warehouse/ItemInward2/PendingStockTransfer/thunk";

type PendingStockTransferProps = {
  loading: boolean;
  pageIndex: number;
  pageSize: number;
  sorting: SortingState;
  columnFilters: any[];
  filterValue: any[];
  search: string;
  searchInput: string;
  warehouseIdSearch: string;
  sortField: string;
  sortDirection: string;
  setSearchInput: React.Dispatch<React.SetStateAction<string>>;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  setPageIndex: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  setColumnFilters: React.Dispatch<React.SetStateAction<any[]>>;
  setFilterValue: React.Dispatch<React.SetStateAction<any[]>>;
  setWarehouseIdSearch: React.Dispatch<React.SetStateAction<string>>;
};

const PendingStockTransfer: React.FC<PendingStockTransferProps> = ({
  loading,
  pageIndex,
  pageSize,
  sorting,
  columnFilters,
  filterValue,
  search,
  searchInput,
  warehouseIdSearch,
  sortField,
  sortDirection,
  setSearch,
  setSearchInput,
  setPageIndex,
  setPageSize,
  setSorting,
  setColumnFilters,
  setFilterValue,
  setWarehouseIdSearch,
}) => {
  document.title = "Pending Stock Transfer";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  // Redux selectors
  const selectPendingStockTransfer = createSelector(
    (state: any) => state.pendingStockTransfer,
    (item) => ({
      list: item.list,
      rowCount: item.rowCount,
      loading: item.loading,
      error: item.error,
    })
  );

  const {
    list,
    rowCount,
    loading: tableLoading,
    error,
  } = useSelector(selectPendingStockTransfer);

  // Use custom hook to fetch and access permissions
  const { canUpdate } = usePermission(
    app_modules?.warehouse?.app_name,
    app_modules?.warehouse?.modules?.item_inward
  );

  const data = list;

  // Fetch Pending Stock Transfer list
  useEffect(() => {
    dispatch(
      fetchPendingStockTransferList({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? (sortDirection as "asc" | "desc") : "",
        search,
        filters: filterValue,
        warehouse_id: warehouseIdSearch || "",
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    filterValue,
    warehouseIdSearch,
  ]);

  // Create menu items for DynamicMenuItem
  const menuItems: MenuItemConfig[] = useMemo(() => {
    const items: MenuItemConfig[] = [
      {
        icon: InputIcon,
        label: "Inward",
        permission: canUpdate,
        onClick: (stockTransfer: any) => {
          console.log(stockTransfer);
          navigate(
            `/warehouse-stock-transfer-item-inward-grn/${stockTransfer?.dispatch_id}`
          );
        },
        color: "primary",
      },
    ];
    return items;
  }, [navigate, canUpdate]);

  const DynamicAction = useMemo(() => {
    // Only show Action column if user has at least one permission
    const hasAnyMenuItemPermission = menuItems.some(
      (item) => item.permission === true
    );

    if (!hasAnyMenuItemPermission) return [];

    return [
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          stickyRight: true,
        },
        cell: ({ row }: { row: any }) => (
          <DynamicMenuItem row={row.original} menuItems={menuItems} />
        ),
      },
    ];
  }, [menuItems]);

  // Listing tanstack table columns
  const columns = useMemo(
    () => [
      {
        header: "Sr. No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          return (
            row.original.sr_no || row.index + 1 + pageIndex * pageSize || "-"
          );
        },
      },
      {
        header: "Dispatch ID",
        accessorKey: "dispatch_number",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${
          api.warehouse2.pendingStockTransfer.dropdown
        }dispatch_number/?warehouse_id=${
          warehouseIdSearch || ""
        }`,
        cell: ({ row }: { row: any }) => row.original.dispatch_number || "-",
      },
      {
        header: "Transfer Date",
        accessorKey: "dispatch_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        filterField: "dispatch_date",
        cell: ({ row }: { row: any }) => {
          const date = row.original.dispatch_date
            ? new Date(row.original.dispatch_date).toISOString().split("T")[0]
            : "-";
          return date;
        },
      },
      {
        header: "Transfer From",
        accessorKey: "transfer_from_warehouse_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${
          api.warehouse2.pendingStockTransfer.dropdown
        }transfer_from_warehouse_name/?warehouse_id=${
          warehouseIdSearch || ""
        }`,
        cell: ({ row }: { row: any }) =>
          row.original.transfer_from_warehouse?.warehouse_name || "-",
      },
      {
        header: "Transfer To",
        accessorKey: "transfer_to_warehouse_name",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${
          api.warehouse2.pendingStockTransfer.dropdown
        }transfer_to_warehouse_name/?warehouse_id=${
          warehouseIdSearch || ""
        }`,
        cell: ({ row }: { row: any }) =>
          row.original.transfer_to_warehouse?.warehouse_name || "-",
      },
      {
        header: "Remarks",
        accessorKey: "remarks",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <TruncatedTextCell
            text={row?.original?.remarks}
            maxWidth="300px"
            maxLines={4}
            rowIndex={row.index}
            tooltipId={`remarks-tooltip-${row.index}`}
          />
        ),
      },
      {
        header: "Created By / Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_user?.username || "-";
          const date = row.original.created_at
            ? new Date(row.original.created_at).toISOString().split("T")[0]
            : "-";
          return `${name} - ${date}`;
        },
      },
      {
        header: "Updated By / Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_user?.username || "-";
          const date = row.original.updated_at
            ? new Date(row.original.updated_at).toISOString().split("T")[0]
            : "-";
          return `${name} - ${date}`;
        },
      },
      ...DynamicAction,
    ],
    [
      pageIndex,
      pageSize,
      warehouseIdSearch,
      DynamicAction,
    ]
  );

  return (
    <>
      <style>
        {`
          .truncated-text-cell-wrapper {
            min-width: 0 !important;
            display: -webkit-box !important;
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
            white-space: normal !important;
            line-height: 1.5em !important;
          }
        `}
      </style>
      <Row id="invoiceList">
        <Col lg={12}>
          <Card>
          <Card.Body className="mt-3">
            <LoaderErrorWrapper loading={loading || tableLoading} error={error}>
              <TableContainer2
                columns={columns}
                data={data}
                totalRecords={rowCount}
                pageIndex={pageIndex}
                pageSize={pageSize}
                onPageChange={setPageIndex}
                onPageSizeChange={setPageSize}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                colValue={setFilterValue}
                sorting={sorting}
                onSortingChange={setSorting}
                isBordered={false}
                isPagination={true}
                isGlobalFilter={false}
                SearchPlaceholder="Search..."
                manualSorting={true}
              />
            </LoaderErrorWrapper>
          </Card.Body>
        </Card>
      </Col>
    </Row>
    </>
  );
};

export default PendingStockTransfer;
